import { useScrollReveal } from '@/hooks/useScrollReveal';

interface Engagement {
  id: string;
  date: string;
  organization: string;
  role: string;
  production: string;
  location: string;
  url: string;
  status: string;
}

function parseDates(dateStr: string) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const month = months.find((m) => dateStr.includes(m)) || '';
  const yearMatch = dateStr.match(/\d{4}/);
  const year = yearMatch ? yearMatch[0] : '';

  // Extract all day numbers from the string
  const dayMatches = dateStr.matchAll(/(\d{1,2})(?:,|\s)/g);
  const days: string[] = [];
  for (const match of dayMatches) {
    const d = match[1];
    if (d && !days.includes(d)) days.push(d);
  }

  return { month, year, days };
}

export default function EngagementSection({
  engagement,
  variant,
}: {
  engagement: Engagement;
  variant: 'light' | 'dark';
}) {
  const ref = useScrollReveal<HTMLDivElement>(0.12, 'fade-up');
  const { month, year, days } = parseDates(engagement.date);

  const c = variant === 'light' ? {
    bg: 'bg-background-50',
    day: 'text-foreground-950',
    month: 'text-foreground-800',
    year: 'text-foreground-500',
    labelText: 'text-foreground-400',
    labelLine: 'bg-foreground-300',
    title: 'text-foreground-950',
    role: 'text-foreground-700',
    smallLabel: 'text-foreground-400',
    body: 'text-foreground-600',
    link: 'text-foreground-600',
    linkHover: 'group-hover:text-foreground-950',
    arrowBorder: 'border-foreground-200',
    arrowBorderHover: 'group-hover:border-foreground-400',
    arrowIcon: 'text-foreground-600',
    hDivider: 'bg-foreground-200',
    vDivider: 'bg-foreground-200',
  } : {
    bg: 'bg-background-950',
    day: 'text-foreground-50',
    month: 'text-foreground-100',
    year: 'text-foreground-300',
    labelText: 'text-foreground-300',
    labelLine: 'bg-foreground-50/30',
    title: 'text-foreground-50',
    role: 'text-foreground-200',
    smallLabel: 'text-foreground-400',
    body: 'text-foreground-200',
    link: 'text-foreground-300',
    linkHover: 'group-hover:text-foreground-50',
    arrowBorder: 'border-foreground-50/20',
    arrowBorderHover: 'group-hover:border-foreground-50/50',
    arrowIcon: 'text-foreground-50',
    hDivider: 'bg-foreground-50/15',
    vDivider: 'bg-foreground-50/10',
  };

  const isMultiDay = days.length > 1;

  return (
    <section className={`relative min-h-[28vh] md:min-h-[32vh] flex items-center ${c.bg}`}>
      <div
        ref={ref}
        className="w-full px-6 md:px-10 lg:px-16 py-8 md:py-12"
      >
        <div className="max-w-5xl mx-auto">
          {/* Date */}
          <div className="flex items-baseline gap-3 md:gap-5 mb-5 md:mb-8">
            {isMultiDay ? (
              <>
                <span className={`text-4xl md:text-6xl lg:text-7xl font-heading ${c.day} leading-none tracking-tight select-none`}>
                  {days.join(' \u2022 ')}
                </span>
                <div className="flex flex-col pb-1 md:pb-2">
                  <span className={`text-base md:text-xl lg:text-2xl ${c.month} tracking-[0.15em] uppercase font-medium`}>
                    {month}
                  </span>
                  <span className={`text-xs md:text-sm lg:text-base ${c.year} tracking-wider`}>
                    {year}
                  </span>
                </div>
              </>
            ) : (
              <>
                <span className={`text-[5rem] md:text-[7rem] lg:text-[9rem] font-heading ${c.day} leading-none tracking-tight select-none`}>
                  {days[0] || ''}
                </span>
                <div className="flex flex-col pb-1 md:pb-3">
                  <span className={`text-lg md:text-2xl lg:text-3xl ${c.month} tracking-[0.15em] uppercase font-medium`}>
                    {month}
                  </span>
                  <span className={`text-sm md:text-lg lg:text-xl ${c.year} tracking-wider`}>
                    {year}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Elegant divider */}
          <div className={`w-full h-[1px] ${c.hDivider} mb-6 md:mb-8`} />

          {/* Production — full width, single line */}
          <div className="mb-5 md:mb-6">
            <p className={`text-[10px] tracking-[0.25em] uppercase ${c.smallLabel} font-medium mb-2`}>
              Production
            </p>
            <h3 className={`font-heading text-xl md:text-2xl lg:text-3xl ${c.title} leading-tight whitespace-nowrap`}>
              {engagement.production}
            </h3>
          </div>

          {/* Divider */}
          <div className={`w-full h-[1px] ${c.hDivider} mb-5 md:mb-6`} />

          {/* Role, Organization, Location, Details */}
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-5">
            <div className="flex-1 min-w-0">
              <p className={`text-[10px] tracking-[0.25em] uppercase ${c.smallLabel} font-medium mb-1`}>
                Role
              </p>
              <p className={`text-base md:text-lg lg:text-xl ${c.role} font-heading italic whitespace-nowrap`}>
                {engagement.role}
              </p>
            </div>

            <div className={`hidden md:block w-[1px] h-10 ${c.vDivider} shrink-0`} />

            <div className="flex-1 min-w-0">
              <p className={`text-[10px] tracking-[0.25em] uppercase ${c.smallLabel} font-medium mb-1`}>
                Organization
              </p>
              <p className={`text-xs md:text-sm ${c.body} font-heading leading-snug`}>
                {engagement.organization}
              </p>
            </div>

            <div className={`hidden md:block w-[1px] h-10 ${c.vDivider} shrink-0`} />

            <div className="flex-1 min-w-0">
              <p className={`text-[10px] tracking-[0.25em] uppercase ${c.smallLabel} font-medium mb-1`}>
                Location
              </p>
              <p className={`text-sm md:text-base ${c.body} whitespace-nowrap`}>
                {engagement.location}
              </p>
            </div>

            {engagement.url && (
              <>
                <div className={`hidden md:block w-[1px] h-10 ${c.vDivider} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <a
                    href={engagement.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 group cursor-pointer"
                  >
                    <span className={`${c.link} text-sm tracking-[0.12em] uppercase ${c.linkHover} transition-colors duration-300 whitespace-nowrap`}>
                      Details
                    </span>
                    <span className={`w-8 h-8 rounded-full border ${c.arrowBorder} flex items-center justify-center ${c.arrowBorderHover} group-hover:scale-110 transition-all duration-300 shrink-0`}>
                      <i className={`ri-arrow-right-up-line ${c.arrowIcon} text-sm`}></i>
                    </span>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}